/** Renders an event's when/where/category box. Shared by PostCard and TranslationCard. */
import { useTranslation } from 'react-i18next'
import { Calendar, MapPin, Navigation, CircleArrowOutUpRight } from 'lucide-react'
import type { EventPost, PostCategory } from '@olimhub/shared'
import { formatDateTime } from '@/lib/dates'
import { buildMapsUrl } from '@/lib/maps'
import { useIsDesktop } from '@/hooks'
import { CategoryChip } from './CategoryChip'

export function EventMeta({ event, category, distanceKm }: { event: EventPost['event']; category?: PostCategory | null; distanceKm?: number }) {
  const { t } = useTranslation()
  const isMobile = !useIsDesktop()
  const startFormatted = formatDateTime(event.startsAt, { autoHideTime: true })
  const endFormatted = event.endsAt ? formatDateTime(event.endsAt, { autoHideTime: true }) : null

  return (
    <div
      style={{
        margin: '12px 16px 0',
        padding: '12px 14px',
        background: 'color-mix(in srgb, var(--accent) 7%, var(--surface))',
        border: '1px solid color-mix(in srgb, var(--accent) 24%, var(--border))',
        borderRadius: 'var(--radius-md)',
        display: 'flex',
        flexDirection: 'column',
        gap: 6,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
        <Calendar size={17} color="var(--accent)" strokeWidth={1.8} />
        <span
          style={{
            fontFamily: 'var(--font-sans)',
            fontSize: 'var(--fs-sm)',
            color: 'var(--text-strong)',
            fontWeight: 600,
          }}
        >
          {startFormatted}
          {endFormatted && ` → ${endFormatted}`}
        </span>
      </div>

      {event.placeText && (isMobile ? (
        <a
          href={buildMapsUrl(event.placeText)}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={t('search.open_in_maps')}
          onClick={(e) => e.stopPropagation()}
          style={{ display: 'flex', alignItems: 'center', gap: 7, textDecoration: 'none' }}
        >
          <MapPin size={17} color="var(--accent)" strokeWidth={1.8} style={{ flexShrink: 0 }} />
          <span style={{ fontFamily: 'var(--font-sans)', fontSize: 'var(--fs-sm)', color: 'var(--accent)', textDecoration: 'underline', textUnderlineOffset: 2 }}>
            {event.placeText}
          </span>
          <CircleArrowOutUpRight size={14} color="var(--accent)" strokeWidth={1.8} style={{ flexShrink: 0 }} />
        </a>
      ) : (
        <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
          <MapPin size={17} color="var(--accent)" strokeWidth={1.8} />
          <span style={{ fontFamily: 'var(--font-sans)', fontSize: 'var(--fs-sm)', color: 'var(--text-body)' }}>
            {event.placeText}
          </span>
        </div>
      ))}

      {distanceKm !== undefined && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
          <Navigation size={17} color="var(--accent)" strokeWidth={1.8} />
          <span style={{ fontFamily: 'var(--font-sans)', fontSize: 'var(--fs-sm)', color: 'var(--text-muted)' }}>
            {t('search.location_distance', { km: distanceKm })}
          </span>
        </div>
      )}

      {category && (
        <div>
          <CategoryChip category={category} />
        </div>
      )}
    </div>
  )
}
